import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import PageMeta from "../components/common/PageMeta";
import { Building, formatBuildingAddress } from "../services/buildingService";
import { useBuilding } from "../context/BuildingContext";
import { useAuth } from "../context/AuthContext";
import { isAdministrator, isPropertyManager } from "../utils/access";

type StatusFilter = "active" | "archived" | "all";

export default function Sites() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { buildings, isLoading, error, selectBuilding } = useBuilding();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");

  const isAdmin = isAdministrator(user);
  const isReadOnlyManager = isPropertyManager(user);
  const accessDenied = Boolean((location.state as { accessDenied?: boolean } | null)?.accessDenied);

  useEffect(() => {
    if (!isLoading && isReadOnlyManager && buildings.length === 1) {
      selectBuilding(buildings[0]);
      navigate(`/sites/${buildings[0].id}`, { replace: true });
    }
  }, [buildings, isLoading, isReadOnlyManager, navigate, selectBuilding]);

  const handleBuildingSelect = (building: Building) => {
    selectBuilding(building);
    navigate(`/sites/${building.id}`);
  };

  const filteredBuildings = buildings.filter((building) => {
    // Search filter
    const matchesSearch =
      building.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (building.street_name && building.street_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (building.full_address && building.full_address.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (building.city && building.city.toLowerCase().includes(searchQuery.toLowerCase()));

    // Status filter (only applies to admins, non-admins only see active)
    // is_hide=1 means archived, is_hide=0 or null means active
    let matchesStatus = true;
    if (isAdmin) {
      if (statusFilter === "active") matchesStatus = building.is_hide !== 1;
      else if (statusFilter === "archived") matchesStatus = building.is_hide === 1;
      // "all" shows everything
    } else {
      // Non-admins only see active buildings
      matchesStatus = building.is_hide !== 1;
    }

    return matchesSearch && matchesStatus;
  });

  const activeCount = buildings.filter((b) => b.is_hide !== 1).length;
  const archivedCount = buildings.filter((b) => b.is_hide === 1).length;

  return (
    <>
      <PageMeta
        title="Sites | SBM Package Admin"
        description="Manage building sites"
      />

      <div className="space-y-6">
        {accessDenied && (
          <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            You do not have access to that page or building.
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">
            Sites
          </h1>
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search here..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:w-64"
            />
          </div>
        </div>

        {/* Status Filter Tabs - Admin Only */}
        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter("active")}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                statusFilter === "active"
                  ? "bg-brand-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
            >
              Active ({activeCount})
            </button>
            <button
              onClick={() => setStatusFilter("archived")}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                statusFilter === "archived"
                  ? "bg-brand-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
            >
              Archived ({archivedCount})
            </button>
            <button
              onClick={() => setStatusFilter("all")}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                statusFilter === "all"
                  ? "bg-brand-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
            >
              All ({buildings.length})
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
          </div>
        )}

        {/* Buildings Grid */}
        {!isLoading && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredBuildings.map((building) => (
              <button
                key={building.id}
                onClick={() => handleBuildingSelect(building)}
                className="group overflow-hidden rounded-2xl border border-gray-200 bg-white text-left transition-shadow hover:shadow-lg dark:border-gray-800 dark:bg-white/[0.03]"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={building.image_url || "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=400"}
                    alt={building.name}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                  {building.is_hide === 1 && (
                    <div className="absolute right-2 top-2 rounded-full bg-amber-500 px-2 py-1 text-xs font-medium text-white">
                      Archived
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 dark:text-white/90">
                    {building.name}
                  </h3>
                  <p className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <svg
                      className="mr-1 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {formatBuildingAddress(building)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredBuildings.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <svg
              className="h-16 w-16 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <p className="mt-4 text-gray-500 dark:text-gray-400">
              {searchQuery
                ? "No buildings match your search"
                : isReadOnlyManager
                  ? "No buildings are assigned to your account."
                  : "No buildings found"}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
